'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as Dialog from '@radix-ui/react-dialog'

import { SearchFooter } from './components/SearchFooter'
import { SearchHeader } from './components/SearchHeader'
import { SearchResults } from './components/SearchResults'
import { DEFAULT_SEARCH_PLACEHOLDER } from './const/defaultSearchPlaceholder.const'
import type { SearchModalItem } from './interfaces/modalItem.interface'
import type { SearchModalProps } from './SearchModal.types'
import { flattenSearchSections } from './utils/flattenSearchSections'

import styles from './SearchModal.module.scss'

const NO_SELECTION = -1

export function SearchModal({
  open,
  onOpenChange,
  query,
  onQueryChange,
  tabs,
  activeTabId,
  onTabChange,
  filterChips,
  activeFilterChipId,
  onFilterChipChange,
  sections,
  isLoading = false,
  emptyState,
  placeholder = DEFAULT_SEARCH_PLACEHOLDER,
  onItemSelect,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(NO_SELECTION)
  const resolvedFilterChipId = activeFilterChipId ?? null

  const flatRows = useMemo(() => flattenSearchSections(sections), [sections])
  const flatCount = flatRows.length

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }

    return undefined
  }, [open])

  useEffect(() => {
    setSelectedIndex(NO_SELECTION)
  }, [query, activeTabId, resolvedFilterChipId, open])

  useEffect(() => {
    if (flatCount === 0) {
      setSelectedIndex(NO_SELECTION)
      return
    }

    setSelectedIndex((prev) => (prev === NO_SELECTION ? NO_SELECTION : Math.min(prev, flatCount - 1)))
  }, [flatCount, sections])

  const handleActivateItem = useCallback(
    (item: SearchModalItem) => {
      onItemSelect?.(item)
    },
    [onItemSelect]
  )

  const moveSelection = useCallback(
    (direction: 1 | -1) => {
      if (flatCount === 0) {
        return
      }

      setSelectedIndex((prev) => {
        if (prev === NO_SELECTION) {
          return direction === 1 ? 0 : flatCount - 1
        }

        return (prev + direction + flatCount) % flatCount
      })
    },
    [flatCount]
  )

  const activateSelection = useCallback(() => {
    const picked = flatRows[selectedIndex]?.item
    if (picked !== undefined) {
      handleActivateItem(picked)
    }
  }, [flatRows, selectedIndex, handleActivateItem])

  const handleNavigationKeyDown = useCallback(
    (event: { key: string; preventDefault: () => void }) => {
      if (flatCount === 0) {
        return
      }

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          moveSelection(1)
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          moveSelection(-1)
          break
        }
        case 'Enter': {
          if (selectedIndex === NO_SELECTION) {
            return
          }
          event.preventDefault()
          activateSelection()
          break
        }
        case 'Escape': {
          event.preventDefault()
          onOpenChange(false)
          break
        }
        default:
          break
      }
    },
    [flatCount, selectedIndex, moveSelection, activateSelection, onOpenChange]
  )

  useEffect(() => {
    if (!open) {
      return
    }

    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') {
        return
      }

      if (flatCount === 0) {
        return
      }

      if (event.key === 'Enter' && selectedIndex === NO_SELECTION) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (event.key === 'ArrowDown') {
        moveSelection(1)
        return
      }

      if (event.key === 'ArrowUp') {
        moveSelection(-1)
        return
      }

      activateSelection()
    }

    window.addEventListener('keydown', onWindowKeyDown, true)
    return () => window.removeEventListener('keydown', onWindowKeyDown, true)
  }, [open, flatCount, selectedIndex, moveSelection, activateSelection])

  const showEmptyPlaceholder = flatCount === 0 && !isLoading && emptyState !== undefined

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <div className={styles.portalRoot}>
          <Dialog.Overlay className={styles.overlay} />
          <Dialog.Content aria-describedby={undefined} className={styles.panel} data-testid="global-search-dialog">
            <Dialog.Title className={styles.visuallyHidden}>Application search</Dialog.Title>

            <div className={styles.mobileHandleWrap} aria-hidden>
              <div className={styles.mobileHandle} />
            </div>

            <SearchHeader
              activeTabId={activeTabId}
              inputRef={inputRef}
              isLoading={isLoading}
              placeholder={placeholder}
              query={query}
              tabs={tabs}
              onKeyDown={handleNavigationKeyDown}
              onQueryChange={onQueryChange}
              onTabChange={onTabChange}
            />

            <SearchResults
              activeFilterChipId={activeFilterChipId}
              emptyState={emptyState}
              filterChips={filterChips}
              isLoading={isLoading}
              query={query}
              sections={sections}
              selectedIndex={selectedIndex}
              showEmptyPlaceholder={showEmptyPlaceholder}
              onFilterChipChange={onFilterChipChange}
              onItemHover={setSelectedIndex}
              onItemSelect={handleActivateItem}
            />

            <SearchFooter />
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
